import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Bootstrap, DataSource, Principal } from "../api/dataSource";
import { startDataSource } from "../api/createDataSource";
import { busiestSite, type Site } from "../domain/sites";
import { config } from "../config/env";

/**
 * Everything a screen needs that is decided once per page load: the adapter,
 * the signed-in principal, the sites, and which site is selected.
 */
export interface Session {
  source: DataSource;
  principal: Principal;
  sites: Site[];
  site: Site;
  selectSite: (id: string) => void;
  /** When the bootstrap was received; ages are computed against it, not a wrong laptop clock drifting mid-session. */
  serverTime: string;
  tenant?: string;
  developmentAuth: boolean;
  /** Present while the mock adapter is standing in for an unreachable platform. */
  fallbackReason?: string;
}

const SessionCtx = createContext<Session | null>(null);

type State =
  | { status: "starting" }
  | { status: "failed"; error: Error }
  | { status: "ready"; source: DataSource; bootstrap: Bootstrap; fallbackReason?: string };

export function SessionProvider({
  children,
  fallback,
  failure,
}: {
  children: ReactNode;
  fallback: ReactNode;
  failure: (error: Error, retry: () => void) => ReactNode;
}) {
  const [state, setState] = useState<State>({ status: "starting" });
  const [attempt, setAttempt] = useState(0);
  const [siteId, setSiteId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    startDataSource()
      .then((s) => {
        if (cancelled) return;
        setState({ status: "ready", source: s.source, bootstrap: s.bootstrap, fallbackReason: s.fallbackReason });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setState({ status: "failed", error: e instanceof Error ? e : new Error(String(e)) });
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const session = useMemo<Session | null>(() => {
    if (state.status !== "ready") return null;
    const { sites } = state.bootstrap;
    const chosen =
      sites.find((s) => s.id === siteId) ??
      sites.find((s) => s.code === config.defaultSiteCode && !siteId) ??
      busiestSite(sites);
    if (!chosen) return null;
    return {
      source: state.source,
      principal: state.bootstrap.principal,
      sites,
      site: chosen,
      selectSite: setSiteId,
      serverTime: state.bootstrap.receivedAt,
      tenant: state.bootstrap.tenant?.name,
      developmentAuth: state.bootstrap.developmentAuth,
      fallbackReason: state.fallbackReason,
    };
  }, [state, siteId]);

  const retry = () => {
    setState({ status: "starting" });
    setAttempt((a) => a + 1);
  };

  if (state.status === "starting") return <>{fallback}</>;
  if (state.status === "failed") return <>{failure(state.error, retry)}</>;
  if (!session) {
    return <>{failure(new Error("The platform returned no sites for this principal."), retry)}</>;
  }
  return <SessionCtx.Provider value={session}>{children}</SessionCtx.Provider>;
}

export function useSession(): Session {
  const s = useContext(SessionCtx);
  if (!s) throw new Error("useSession must be used inside SessionProvider");
  return s;
}

/** The session if there is one — for a screen that also works on a bench with no provider. */
export function useSessionIfAny(): Session | null {
  return useContext(SessionCtx);
}
